import { useEffect, useState, useCallback } from 'react';
import { api, apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/Bits';
import { Card, CardContent } from '@/components/ui/card';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  PencilEdit01Icon,
  Add01Icon,
  Building06Icon,
  UserMultipleIcon,
  Settings02Icon,
  Cancel01Icon,
  Calendar03Icon,
} from '@hugeicons/core-free-icons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ButtonGroup } from '@/components/ui/button-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * TenantsTab — Vista de administración de ámbitos (tenants).
 * Solo visible para super admin.
 * CRUD de tenants + configuración de módulos por tenant.
 */
export function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create/Edit dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [saving, setSaving] = useState(false);

  // Modules dialog state
  const [modulesDialogOpen, setModulesDialogOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [modules, setModules] = useState([]);
  const [savingModules, setSavingModules] = useState(false);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenants');
      setTenants(data || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const openCreateDialog = () => {
    setEditingTenant(null);
    setFormName('');
    setFormSlug('');
    setIsDialogOpen(true);
  };

  const openEditDialog = (tenant) => {
    setEditingTenant(tenant);
    setFormName(tenant.name);
    setFormSlug(tenant.slug);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!formSlug.trim()) {
      toast.error('El slug es obligatorio');
      return;
    }

    setSaving(true);
    try {
      if (editingTenant) {
        await api.put(`/tenants/${editingTenant.id}`, {
          name: formName.trim(),
          slug: formSlug.trim(),
        });
        toast.success('Ámbito actualizado');
      } else {
        await api.post('/tenants', {
          name: formName.trim(),
          slug: formSlug.trim(),
        });
        toast.success('Ámbito creado');
      }
      setIsDialogOpen(false);
      fetchTenants();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (tenantId) => {
    try {
      await api.put(`/tenants/${tenantId}/deactivate`);
      toast.success('Ámbito desactivado');
      fetchTenants();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openModulesDialog = async (tenantId) => {
    setSelectedTenantId(tenantId);
    try {
      const { data } = await api.get(`/tenants/${tenantId}/modules`);
      setModules(data || []);
      setModulesDialogOpen(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const handleModuleToggle = (category, enabled) => {
    setModules((prev) => prev.map((m) => (m.category === category ? { ...m, enabled } : m)));
  };

  const handleSaveModules = async () => {
    setSavingModules(true);
    try {
      await api.put(`/tenants/${selectedTenantId}/modules`, modules);
      toast.success('Módulos actualizados');
      setModulesDialogOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingModules(false);
    }
  };

  // Auto-generate slug from name
  const handleNameChange = (value) => {
    setFormName(value);
    if (!editingTenant) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 50);
      setFormSlug(slug);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Ámbitos organizacionales</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Espacios que agrupan usuarios, rutas y módulos habilitados.
          </p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2" data-testid="create-tenant-btn">
          <HugeiconsIcon icon={Add01Icon} className="h-4 w-4" />
          Crear Ámbito
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-5">
                <div className="h-5 bg-muted rounded w-1/2 mb-3" />
                <div className="h-3 bg-muted rounded w-1/3 mb-4" />
                <div className="flex gap-4">
                  <div className="h-4 bg-muted rounded w-16" />
                  <div className="h-4 bg-muted rounded w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : tenants.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary/10 mb-4">
              <HugeiconsIcon icon={Building06Icon} className="h-6 w-6 text-secondary" />
            </div>
            <p className="text-sm font-medium">No hay ámbitos configurados</p>
            <p className="text-xs text-muted-foreground mt-1">Cree un ámbito para organizar usuarios y módulos.</p>
            <Button onClick={openCreateDialog} className="gap-2 mt-4" size="sm">
              <HugeiconsIcon icon={Add01Icon} className="h-3.5 w-3.5" />
              Crear primer ámbito
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tenants-table">
          {tenants.map((tenant) => (
            <Card key={tenant.id} className="hover:border-secondary/30 transition-colors">
              <CardContent className="p-4 flex items-center gap-4">
                {/* Left: icon + name + slug + status */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary/10">
                    <HugeiconsIcon icon={Building06Icon} className="h-5 w-5 text-secondary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold truncate">{tenant.name}</h4>
                      <StatusBadge status={tenant.status === 'active' ? 'Activo' : 'Inactivo'} />
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{tenant.slug}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-3 shrink-0">
                  <div
                    className="flex items-center gap-1.5"
                    title={`${tenant.user_count || 0} usuarios en este ámbito`}>
                    <HugeiconsIcon icon={UserMultipleIcon} className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-xs font-medium">{tenant.user_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1.5" title="Fecha de creación">
                    <HugeiconsIcon icon={Calendar03Icon} className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {tenant.created_at
                        ? new Date(tenant.created_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })
                        : '—'}
                    </span>
                  </div>
                </div>

                {/* Actions — icon-only, always visible */}
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => openEditDialog(tenant)}
                    title="Editar ámbito"
                    aria-label={`Editar ámbito ${tenant.name}`}>
                    <HugeiconsIcon icon={PencilEdit01Icon} className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => openModulesDialog(tenant.id)}
                    title="Configurar módulos"
                    aria-label={`Configurar módulos de ${tenant.name}`}>
                    <HugeiconsIcon icon={Settings02Icon} className="h-3.5 w-3.5" />
                  </Button>
                  {tenant.status === 'active' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeactivate(tenant.id)}
                      title="Desactivar ámbito"
                      aria-label={`Desactivar ámbito ${tenant.name}`}>
                      <HugeiconsIcon icon={Cancel01Icon} className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTenant ? 'Editar Ámbito' : 'Crear Ámbito'}</DialogTitle>
            <DialogDescription>
              {editingTenant
                ? 'Modifique el nombre o slug del ámbito.'
                : 'Defina nombre y slug para el nuevo ámbito (organización/cliente).'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="tenant-name">Nombre de la organización</Label>
              <Input
                id="tenant-name"
                value={formName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ej: UC CHRISTUS"
                maxLength={150}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tenant-slug">Slug (identificador URL)</Label>
              <Input
                id="tenant-slug"
                value={formSlug}
                onChange={(e) => setFormSlug(e.target.value)}
                placeholder="Ej: uc-christus"
                maxLength={50}
                className="font-mono"
              />
              <p className="text-[10px] text-muted-foreground">
                Formato: letras minúsculas, números y guiones (kebab-case)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editingTenant ? 'Guardar cambios' : 'Crear ámbito'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modules Configuration Dialog */}
      <Dialog open={modulesDialogOpen} onOpenChange={setModulesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar Módulos</DialogTitle>
            <DialogDescription>Active o desactive módulos (categorías de rutas) para este ámbito.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {modules.map((mod) => (
              <div key={mod.category} className="flex items-center justify-between py-2 px-3 rounded-md border">
                <span className="text-sm font-medium">{mod.category}</span>
                <Switch
                  checked={mod.enabled}
                  onCheckedChange={(checked) => handleModuleToggle(mod.category, checked)}
                  aria-label={`${mod.enabled ? 'Desactivar' : 'Activar'} módulo ${mod.category}`}
                />
              </div>
            ))}
            {modules.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No hay módulos configurados</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModulesDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveModules} disabled={savingModules}>
              {savingModules ? 'Guardando...' : 'Guardar configuración'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
